import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    alpha,
    InputAdornment,
} from '@mui/material';
import axios from 'axios';
import { validateAmountsForm } from './certification.validation';

// Formater un montant dinar en affichage millimes: 1000 → "1 000.000"
const formatMillime = (value) => {
    const num = parseFloat(value) || 0;
    const millimes = Math.round(Math.abs(num) * 1000);
    const str = String(millimes).padStart(4, '0');
    const intPart = str.slice(0, -3);
    const decPart = str.slice(-3);
    const cleanInt = intPart.replace(/^0+/, '') || '0';
    const formattedInt = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (num < 0 ? '-' : '') + formattedInt + '.' + decPart;
};

// Convertir des chiffres bruts (millimes) en string dinar sans floating point
const millimeDigitsToDinar = (digits) => {
    if (!digits) return '';
    return digits.length <= 3
        ? '0.' + digits.padStart(3, '0')
        : digits.slice(0, -3) + '.' + digits.slice(-3);
};

const CertificationAmounts = ({
    clientId,
    beneficiary,
    formData,
    amountsData,
    setAmountsData,
    amountsErrors,
    setAmountsErrors,
    onBack,
    onNext,
    palette,
}) => {
    const [tvaRates, setTvaRates] = useState([]);
    const [loading, setLoading] = useState(false);

    const tauxRS = parseFloat(formData.tauxRS) || 0;
    const tauxRsaTva = formData.rsaTva === 'RSATVA25' ? (parseFloat(formData.tauxRsaTva) || 25) : 0;

    const handleChange = (field, value) => {
        setAmountsData((prev) => ({ ...prev, [field]: value }));
        setAmountsErrors((prev) => ({ ...prev, [field]: '' }));
    };

    // ── Masque millimes pour montantHT ──
    const handleMillimeKeyDown = useCallback((e) => {
        const currentMillimes = Math.round((parseFloat(amountsData.montantHT) || 0) * 1000);
        const currentDigits = currentMillimes === 0 ? '' : String(currentMillimes);

        if (e.key >= '0' && e.key <= '9') {
            e.preventDefault();
            const newDigits = currentDigits + e.key;
            handleChange('montantHT', millimeDigitsToDinar(newDigits));
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            const newDigits = currentDigits.slice(0, -1);
            handleChange('montantHT', newDigits ? millimeDigitsToDinar(newDigits) : '');
        } else if (!['Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(e.key)) {
            e.preventDefault();
        }
    }, [amountsData.montantHT]);

    const handleMillimePaste = useCallback((e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
        if (!pasted) return;
        const currentMillimes = Math.round((parseFloat(amountsData.montantHT) || 0) * 1000);
        const currentDigits = currentMillimes === 0 ? '' : String(currentMillimes);
        const newDigits = currentDigits + pasted;
        handleChange('montantHT', millimeDigitsToDinar(newDigits));
    }, [amountsData.montantHT]);

    // Charger les taux TVA depuis TEJ
    useEffect(() => {
        if (!clientId) return;

        const fetchTvaRates = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/api/certif/tva-rates/${clientId}`);
                const data = response.data?.data || response.data || [];
                setTvaRates(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Erreur chargement taux TVA:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchTvaRates();
    }, [clientId]);

    // Calculer les montants derives
    useEffect(() => {
        const ht = parseFloat(amountsData.montantHT) || 0;
        const tva = parseFloat(amountsData.tauxTVA) || 0;

        const montantTVA = ht * (tva / 100);
        const montantTTC = ht + montantTVA;
        // RS sur TTC, arrondi vers le bas (comme TEJ)
        const montantRS = Math.floor(montantTTC * (tauxRS / 100) * 1000) / 1000;
        const montantRsaTva = montantTVA * (tauxRsaTva / 100);
        const netAPayer = montantTTC - montantRS - montantRsaTva;

        setAmountsData((prev) => ({
            ...prev,
            montantTVA: montantTVA.toFixed(3),
            montantTTC: montantTTC.toFixed(3),
            montantRS: montantRS.toFixed(3),
            montantRsaTva: montantRsaTva.toFixed(3),
            netAPayer: netAPayer.toFixed(3),
        }));
    }, [amountsData.montantHT, amountsData.tauxTVA, tauxRS, tauxRsaTva]);

    const handleSubmit = () => {
        const errors = validateAmountsForm(amountsData);
        setAmountsErrors(errors);
        if (Object.keys(errors).length > 0) return;
        onNext();
    };

    if (loading) {
        return (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const readOnlyFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: alpha(palette.navy, 0.04),
        },
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                border: `1px solid ${alpha(palette.navy, 0.08)}`,
                backgroundColor: alpha('#ffffff', 0.9),
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 700, color: palette.textPrimary, mb: 3 }}>
                Montants et retenues
            </Typography>

            <Grid container spacing={3}>
                {/* Montant HT (masque millimes) */}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        required
                        label="Montant HT"
                        value={amountsData.montantHT ? formatMillime(amountsData.montantHT) : '0.000'}
                        onKeyDown={handleMillimeKeyDown}
                        onPaste={handleMillimePaste}
                        onChange={() => {}}
                        error={!!amountsErrors.montantHT}
                        helperText={amountsErrors.montantHT || 'Saisie en millimes (ex: tapez 1000000 pour 1 000.000 TND)'}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                        }}
                    />
                </Grid>

                {/* Taux TVA - depuis TEJ */}
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="taux-tva-label">Taux TVA</InputLabel>
                        <Select
                            labelId="taux-tva-label"
                            label="Taux TVA"
                            value={amountsData.tauxTVA}
                            onChange={(e) => handleChange('tauxTVA', e.target.value)}
                        >
                            {tvaRates.length > 0 ? (
                                tvaRates.map((rate) => (
                                    <MenuItem key={rate.id} value={String(rate.value)}>
                                        {rate.value}%
                                    </MenuItem>
                                ))
                            ) : (
                                <>
                                    <MenuItem value="0">0%</MenuItem>
                                    <MenuItem value="7">7%</MenuItem>
                                    <MenuItem value="9">9%</MenuItem>
                                    <MenuItem value="13">13%</MenuItem>
                                    <MenuItem value="19">19%</MenuItem>
                                </>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Montant TVA (calcule) */}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Montant TVA"
                        value={formatMillime(amountsData.montantTVA)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                        }}
                        sx={readOnlyFieldSx}
                    />
                </Grid>

                {/* Montant TTC (calcule) */}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Montant TTC"
                        value={formatMillime(amountsData.montantTTC)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                        }}
                        sx={readOnlyFieldSx}
                    />
                </Grid>

                {/* Taux RS (auto depuis l'operation - lecture seule) */}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Taux RS (auto)"
                        value={`${tauxRS}%`}
                        InputProps={{ readOnly: true }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(palette.teal, 0.06),
                            },
                        }}
                    />
                </Grid>

                {/* Montant RS (calcule) */}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Montant retenue a la source"
                        value={formatMillime(amountsData.montantRS)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                        }}
                        sx={readOnlyFieldSx}
                    />
                </Grid>

                {/* RS au titre de TVA (si RSATVA25) */}
                {tauxRsaTva > 0 && (
                    <>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Taux RS au titre de TVA"
                                value={`${tauxRsaTva}%`}
                                InputProps={{ readOnly: true }}
                                sx={readOnlyFieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Montant RS au titre de TVA"
                                value={formatMillime(amountsData.montantRsaTva)}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                                }}
                                sx={readOnlyFieldSx}
                            />
                        </Grid>
                    </>
                )}

                {/* Net a payer (calcule) */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Net a payer"
                        value={formatMillime(amountsData.netAPayer)}
                        InputProps={{
                            readOnly: true,
                            endAdornment: <InputAdornment position="end">TND</InputAdornment>,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(palette.teal, 0.08),
                                fontWeight: 700,
                            },
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onBack}
                            sx={{ minWidth: 120, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                        >
                            Retour
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                                px: 3, py: 1.2, borderRadius: 2, fontWeight: 700, textTransform: 'none',
                                background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                                '&:hover': { background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)` },
                            }}
                        >
                            Suivant
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default CertificationAmounts;
